import _capitalize from 'lodash/capitalize';
import _startCase from 'lodash/startCase';
import enumsMixin from '@/mixins/enums';
import EntryWhereSomeInput from '@src/components/manage/EntryWhereSomeInput';
import gql from 'graphql-tag';
import formatDate from 'date-fns/format';
import LocationSearch from '@/components/misc/LocationSearch';
import Sessions from '@/components/users/Sessions';
import SidebarDetails from '@/components/manage/SidebarDetails';
import { createStore } from '@src/lib/manage';
import { UserWhereManyInput } from '@src/models/where';

const { computed: { userOrderByEnums } } = enumsMixin;

export const icon = 'users';
export const name = 'Users';

export const RelationUserFragment = gql`
  fragment RelationUserDetails on User {
    id
    title: fullName
    emailAddress
    role
    jobTitle: title
    created
    lastActiveAt
    status
  }
`;

export const listSchema = {
  page: {
    title: 'Manage Users',
  },
  query: gql`
    query ListUserEntries($where: UserWhereManyInput, $order: UserOrderByInput) {
      company {
        id
        userConnections(where: $where, order: $order) {
          edges {
            entry {
              ...RelationUserDetails
            }
          }
          total
        }
      }
    }
    ${RelationUserFragment}
  `,
  update({ company: { userConnections: { edges, total } = {} } = {} } = {}) {
    return {
      edges: (Array.isArray(edges) ? edges : []).map(({ entry }) => entry),
      total,
    };
  },
  where: { status: 'ACTIVE' },
  whereOptions: UserWhereManyInput,
  order: 'CREATED_ASC',
  orderOptions: userOrderByEnums,
  primaryKey: 'id',
  columns() {
    const columns = [
      { key: 'title', label: 'Name' },
      { key: 'emailAddress' },
      { key: 'role', formatter: value => _startCase(`${value}`.toLowerCase()) },
      { key: 'jobTitle' },
    ];

    if (!this.modalOpts) {
      columns.push({ key: 'created', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') });
      columns.push({ key: 'status', formatter: value => _capitalize(value) });
    }

    return columns;
  },
  // columnOptions: {
  //   title: { label: 'Name', default: true },
  //   emailAddress: { label: 'Email Address', default: true },
  //   role: { label: 'Role', default: true },
  //   jobTitle: { label: 'Job Title', default: true },
  //   created: { label: 'Created' },
  //   status: { label: 'Status' },
  // },
  option(createElement, { id, icon, title, emailAddress }) {
    return createElement('div', { class: 'd-flex flex-row justify-content-start align-items-center' }, [
      createElement('span', { class: 'bg-dark text-white rounded-circle mr-1' }, [
        createElement('font-awesome-icon', { class: 'mx-1', props: { icon: icon || 'users', fixedWith: true, size: 'sm' } }),
      ]),
      createElement('span', { class: 'd-block' }, title),
      emailAddress ? createElement('small', { class: 'd-block' }, emailAddress) : undefined,
    ]);
  },
  emptyOptionValue() {
    return {
      icon: this.many ? 'users' : 'user',
      title: this.many ? 'Select Users' : 'Select User',
    };
  },
};

const entryFragment = gql`
  fragment ManageUserDetails on User {
    id
    fullName
    firstName
    lastName
    emailAddress
    role
    title

    location {
      id
      name
      countryCode
    }

    departments {
      id
      name
      icon
    }

    customFields {
      key
      value
    }

    referrerUser {
      ...RelationUserDetails
    }

    preferences {
      receiveWeeklyEmails
      receiveReengagementEmails
      visibleInLeaderboards
    }

    sessions(order:UPDATED_DESC) {
      id
      ipAddress
      userAgent
      created
      updated
      status
    }

    created
    lastActiveAt
    updated
    status
  }
  ${RelationUserFragment}
`;

export const editSchema = {
  page: {
    title() {
      return this.value && this.value.fullName
        ? `Edit User - ${this.value.fullName}`
        : (this.entryID === 'new' ? 'New User' : 'Edit User');
    },
    breadcrumb() {
      return this.entryID === 'new' ? 'New' : 'Edit';
    },
  },
  apolloReadEntry: {
    query: gql`
      query ReadUserEntry($entryID: ID!) {
        company {
          id
          value: user(where:{ id: $entryID }) {
            ...ManageUserDetails
          }
        }
      }
      ${entryFragment}
    `,
  },
  apolloCreateEntry: {
    mutation: gql`
      mutation CreateUserEntry($entryID: ID!, $create: CreateUserInput!) {
        value: createUser(create: $create) {
          ...ManageUserDetails
        }
      }
      ${entryFragment}
    `,
  },
  apolloUpdateEntry: {
    mutation: gql`
      mutation UpdateUserEntry($entryID: ID!, $update: UpdateUserInput!) {
        value: updateUser(update: $update, where: { id: $entryID }) {
          ...ManageUserDetails
        }
      }
      ${entryFragment}
    `,
  },
  form: {
    $main: {
      props: {
        data: {
          type: Object,
          default: () => null,
        },
        value: {
          type: Object,
          default: () => ({}),
        },
      },
      render(el) {
        const { value } = this;
        const { isSuperAdmin, isRecruiterAdmin } = this.$root;
        const { getValue, setValue } = createStore.call(this, value);

        const rows = [];

        rows.push(el('b-row', {}, [
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'First Name', 'label-for': 'firstName', 'label-cols-sm': 3 },
            }, [
              el('b-input', {
                props: { value: getValue('firstName') },
                domProps: { id: 'firstName' },
                on: { input: value => setValue('firstName', value) },
              }),
            ]),
            el('b-form-group', {
              props: { label: 'Last Name', 'label-for': 'lastName', 'label-cols-sm': 3 },
            }, [
              el('b-input', {
                props: { value: getValue('lastName') },
                domProps: { id: 'lastName' },
                on: { input: value => setValue('lastName', value) },
              }),
            ]),
          ]),
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'Email Address', 'label-for': 'emailAddress', 'label-cols-sm': 3 },
            }, [
              el('b-input', {
                props: { value: getValue('emailAddress') },
                domProps: { id: 'emailAddress' },
                on: { input: value => setValue('emailAddress', value) },
              }),
            ]),
            el('b-form-group', {
              props: { label: 'Role', 'label-for': 'role', 'label-cols-sm': 3 },
            }, [
              el('b-form-select', {
                props: {
                  disabled: !isRecruiterAdmin,
                  options: [
                    ...(isSuperAdmin ? [ { text: 'Super Admin', value: 'SUPER_ADMIN' } ] : []),
                    { text: 'Recruiter Admin', value: 'RECRUITER_ADMIN' },
                    { text: 'Recruiter', value: 'RECRUITER' },
                    { text: 'Employee', value: 'EMPLOYEE' },
                  ],
                  value: getValue('role'),
                },
                domProps: { id: 'role' },
                on: { input: value => setValue('role', value) },
              }),
            ]),
          ]),
        ]));

        rows.push(el('b-row', {}, [
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'Departments', 'label-for': 'departments', 'label-cols-sm': 3 },
            }, [
              el(EntryWhereSomeInput, {
                props: {
                  type: 'departments',
                  title: 'Departments',
                  many: true,
                  value: getValue('departments'),
                  where: { status: 'ACTIVE' },
                },
                domProps: { id: 'departments' },
                on: { input: value => setValue('departments', value) },
              }),
            ]),
          ]),
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'Location', 'label-for': 'location', 'label-cols-sm': 3 },
            }, [
              el(LocationSearch, {
                props: { value: getValue('location') },
                domProps: { id: 'location' },
                on: { input: value => setValue('location', value && value.id ? value.id : null) },
              }),
            ]),
          ]),
        ]));

        rows.push(el('b-row', {}, [
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'Job Title', 'label-for': 'title', 'label-cols-sm': 3 },
            }, [
              el('b-input', {
                props: { value: getValue('title') },
                domProps: { id: 'title' },
                on: { input: value => setValue('title', value) },
              }),
            ]),
          ]),
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'Referrer User', 'label-for': 'referrerUser', 'label-cols-sm': 3 },
            }, [
              el(EntryWhereSomeInput, {
                props: {
                  type: 'users',
                  title: 'Referrer User',
                  value: getValue('referrerUser'),
                  where: { role: 'EMPLOYEE', status: 'ACTIVE' },
                },
                domProps: { id: 'referrerUser' },
                on: { input: value => setValue('referrerUser', value) },
              }),
            ]),
          ]),
        ]));

        rows.push(el('b-row', {}, [
          el('b-col', { props: { cols: 12, xl: 6 } }, [
            el('b-form-group', {
              props: { label: 'Preferences', 'label-for': 'preferences', 'label-cols-sm': 3 },
            }, [
              el('b-row', {}, [
                el('b-col', { props: { cols: 12 } }, [
                  el('b-checkbox', {
                    props: { checked: getValue('preferences.receiveWeeklyEmails') },
                    on: { input: value => setValue('preferences.receiveWeeklyEmails', value) },
                  }, [ 'Receive Weekly Emails' ]),
                  el('b-checkbox', {
                    props: { checked: getValue('preferences.receiveReengagementEmails') },
                    on: { input: value => setValue('preferences.receiveReengagementEmails', value) },
                  }, [ 'Receive Reengagement Emails' ]),
                  el('b-checkbox', {
                    props: { checked: getValue('preferences.visibleInLeaderboards') },
                    on: { input: value => setValue('preferences.visibleInLeaderboards', value) },
                  }, [ 'Participate in leaderboards' ]),
                ]),
              ])
            ]),
          ]),
        ]));

        rows.push(el('hr'));

        rows.push(el('b-form-group', { props: { label: 'Sessions' } }, [
          el(Sessions, {
            props: { value: getValue('sessions') },
          }),
        ]));

        return el('div', {}, rows);
      },
    },
    $sidebar: {
      props: {
        data: {
          type: Object,
          default: () => null,
        },
        value: {
          type: Object,
          default: () => ({}),
        },
      },
      render(el) {
        return el('div', { class: 'd-flex flex-column' }, [
          el(SidebarDetails, {
            class: 'my-3',
            props: {
              fields: [
                { key: 'id', label: 'ID' },
                { key: 'created', label: 'Created At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
                { key: 'updated', label: 'Updated At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
                { key: 'status', label: 'Status', formatter: value => _capitalize(`${value}`.toLowerCase()) },
              ],
              value: this.value,
            },
          }),
          // el('b-list-group', { props: { class: 'my-3' } }, [
          //   el('b-list-group-item', { props: { href: '#' } }, [ 'View sessions' ]),
          // ]),
        ]);
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/models/Users.js